<script setup lang="ts">
import type { ITableFilterProps } from '@modules/tables/components/VTableFilter/types';
import type { ITableColumnsOptions } from '@modules/tables/types';
import { transformNumber } from '@/utils/numbers';
import VButton from '@components/buttons/VButton/VButton.vue';
import VCard from '@components/card/VCard/VCard.vue';
import {
  AFF_BALANCES_PERMISSIONS,
  TRANSLATION_GROUP_AFF_BALANCES,
} from '@modules/aff-balances/constants';
import { useDownloadAffBalancesReportMutation } from '@modules/aff-balances/mutations';
import {
  useAffBalancesListQuery,
  useAffiliateBalanceQuery,
} from '@modules/aff-balances/queries';
import { AFFILIATE_BALANCE_WITHDRAWAL_ROUTE_NAME } from '@modules/aff-balances/submodules/withdrawal/constants';
import { useConfig } from '@modules/config/composables';
import VDownloadReportButton from '@modules/download-report/components/VDownloadReportButton/VDownloadReportButton.vue';
import { useDownloadReport } from '@modules/download-report/composables';
import { ESmartFilterColumnType } from '@modules/smart-filters/enums';
import VTable from '@modules/tables/components/VTable/VTable.vue';
import { useTableData } from '@modules/tables/composables';

const {
  tableState,
  isLoading,
  isFetching,
  data,
  updateTableState,
  refreshTableData,
} = useTableData(useAffBalancesListQuery);

const {
  isReportLoading,
  onDownloadReport,
} = useDownloadReport(useDownloadAffBalancesReportMutation(), tableState);

const tableFilterOptions: ITableFilterProps = {
  pathForFilter: 'api2/affiliate/cashouts',
  actionForFilter: 'list',
  byMerchant: true,
  filterName: 'affiliate_cashout',
};

const columnOptions: ITableColumnsOptions = {
  merchant_name: {
    type: ESmartFilterColumnType.CHECKBOX,
  },
  created_at: {
    type: ESmartFilterColumnType.DATE,
  },
  comment: {
    type: ESmartFilterColumnType.CHECKBOX,
  },
  amount: {
    type: ESmartFilterColumnType.COMPARE,
    defaultMobilePreview: true,
  },
  state: {
    type: ESmartFilterColumnType.CHECKBOX,
    localization: {
      translationPath: 'affiliate.balance.statuses.',
    },
  },
  payment_system_name: {
    type: ESmartFilterColumnType.CHECKBOX,
  },
  payment_system_details: {
    type: ESmartFilterColumnType.CHECKBOX,
  },
};

const { config } = useConfig();
const { data: balance, isLoading: isLoadingBalance } = useAffiliateBalanceQuery();
</script>

<template>
  <div v-data-qa-auto-id="'AffBalancesListPage'">
    <div
      v-if="!isLoadingBalance && balance !== undefined && $hasPermission(AFF_BALANCES_PERMISSIONS.SHOW_BALANCE)"
      class="flex"
    >
      <VCard class="mb-4 min-w-[220px]">
        <div class="text-5xl font-medium">
          {{ config?.currency }} {{ transformNumber(balance) }}
        </div>
        <VButton
          v-if="$hasPermission(AFF_BALANCES_PERMISSIONS.WITHDRAW)"
          class="mt-6"
          :label="$getTranslation(TRANSLATION_GROUP_AFF_BALANCES, 'WITHDRAWAL_BUTTON')"
          :to="{
            name: AFFILIATE_BALANCE_WITHDRAWAL_ROUTE_NAME,
          }"
        />
      </VCard>
    </div>
    <VCard type="table">
      <VTable
        :data="data"
        :state="tableState"
        :loading="isLoading"
        :is-fetching="isFetching"
        :title-t-path="$getTPath(TRANSLATION_GROUP_AFF_BALANCES, 'HEADER')"
        :filter-props="tableFilterOptions"
        :columns-options="columnOptions"
        @state-update="updateTableState"
        @refresh="refreshTableData"
      >
        <template
          v-if="$hasPermission(AFF_BALANCES_PERMISSIONS.DOWNLOAD_LIST)"
          #footerButtons
        >
          <VDownloadReportButton
            :label="$getTranslation(TRANSLATION_GROUP_AFF_BALANCES, 'DOWNLOAD_REPORT')"
            :loading="isReportLoading"
            @download="onDownloadReport"
          />
        </template>
      </VTable>
    </VCard>
  </div>
</template>
